/** Hooks gestion des comptes admin : administrateurs et chefs d'équipe. */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const API_URL = import.meta.env.VITE_API_URL;

function headers(): HeadersInit {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

import type { EmployeeActivity } from '../types';

export type UserRole = 'admin' | 'manager';

export interface AppUser {
  id: number;
  username: string;
  email: string;
  role: UserRole;
  // Équipe gérée (chefs d'équipe uniquement, null = non restreint)
  activity: EmployeeActivity | null;
  is_active: boolean;
}

export function useUsers() {
  return useQuery({
    queryKey: ['users'],
    queryFn: async (): Promise<AppUser[]> => {
      const res = await fetch(`${API_URL}/api/users`, { headers: headers() });
      if (!res.ok) throw new Error('Erreur lors du chargement des utilisateurs');
      return res.json();
    },
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      username: string;
      email: string;
      password: string;
      role: UserRole;
      activity?: EmployeeActivity | null;
    }) => {
      const res = await fetch(`${API_URL}/api/users`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Erreur');
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`${API_URL}/api/users/${id}`, {
        method: 'DELETE',
        headers: headers(),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Erreur');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}
